import isObject from 'lodash/isObject';
import moment from 'moment';

import { FlowType, ServiceSlugs, WorkflowAccessType, WorkflowPermissions } from '@ply-io/ply-front-shared-utilities';

import { needToShowWorkflow } from './_workflow-service-ts';
import { isTileElement } from './element-service';
import { capitalizeWord } from '@/utils/language-service';
import { SHAREABLE_FLOW_TYPES } from '@/utils/workflow-service.const';
import { getToolPlacementTitle, isCommandBarTool, isSlackTool } from '@/utils/workflow-tool-service.js';

export const DEFAULT_SAMPLE_CUSTOMER_EMAIL = 'customer@gmail.com';

const getWorkflowName = (flow) => (flow ? flow.workflow_title || `Flow (#${flow.id})` : '');

const canHideWorkflow = (flowData) => flowData.permissions.includes(WorkflowPermissions.SwitchOnOffWorkflow);

const buildCustomerData = (flow) => {
	const email = flow.current_draft.sample_data?.customer?.email;
	return { email };
};

const getGlobalMappedFields = (flow, user, backwardComp = false) => {
	const curDate = new Date();
	const keepOffset = true;
	const userKey = backwardComp ? 'user_that_triggered_the_flow' : 'running_user';

	const globalMappedFields = {
		workflow: { id: flow.id, name: flow.workflow_title },
		timestamp: curDate.toISOString(),
		timestamp_workspace: user.profile.account.timezone ? moment.tz(curDate, user.profile.account.timezone).toISOString(keepOffset) : null,
		date: curDate.toISOString().split('T')[0],
		time: curDate.toISOString().split('T')[1].split('.')[0],
	};

	if (!isPortal(flow)) {
		// Hide user data in portal
		globalMappedFields[userKey] = {
			first_name: user.first_name,
			last_name: user.last_name,
			email: user.email,
			timezone: user.profile.timezone,
		};

		globalMappedFields['workspace'] = {
			id: user.profile.account.id,
			name: user.profile.account.name,
			timezone: user.profile.account.timezone,
		};
		globalMappedFields['timestamp_user'] = user.profile.timezone ? moment.tz(curDate, user.profile.timezone).toISOString(keepOffset) : null;
	}

	if (isCustomerAuthEnabled(flow)) {
		globalMappedFields['customer'] = buildCustomerData(flow);
	}

	return globalMappedFields;
};

const isWorkflowVersionOfWorkflow = (flow, versionId) => flow.versions.some((ver) => ver.id === versionId);

const isWorkflowShareable = (workflow) => SHAREABLE_FLOW_TYPES.includes(workflow.flow_type);

const getWorkflowContextServiceSlug = (workflow) => workflow?.context_service_slug;

const mapNodesOfWorkflowByField = ({ workflowVersionFullObject, rootGetters, getNode, fieldToMapBy }) => {
	if (workflowVersionFullObject?.nodes) {
		const { nodes } = workflowVersionFullObject;
		const workflowNodes = {};
		nodes.forEach((nodeInstance) => {
			const nodeId = isObject(nodeInstance) ? nodeInstance.id : nodeInstance;
			const workflowNode = rootGetters[getNode.fullPath](nodeId);
			if (workflowNode) {
				workflowNodes[workflowNode[fieldToMapBy]] = workflowNode;
			}
		});
		return workflowNodes;
	}
	return {};
};

const getConfigurationFields = (configurationId, getConfigurationResponse) => {
	const configurationResponse = getConfigurationResponse(configurationId) || {};
	return configurationResponse;
};

const getUserConfigurationFields = (workflowVersion, getConfigurationResponse) =>
	getConfigurationFields(workflowVersion?.user_specific_config_options, getConfigurationResponse);

const getGlobalConfigurationFields = (workflowVersion, getConfigurationResponse) =>
	getConfigurationFields(workflowVersion?.general_config_options, getConfigurationResponse);

const getWorkflowFlowType = (workflow) => workflow?.flow_type;
const isOfTypeTool = (workflow) => getWorkflowFlowType(workflow) === FlowType.Tool;
const isOfTypeFeature = (workflow) => getWorkflowFlowType(workflow) === FlowType.Experience;
const isOfTypeCloud = (workflow) => getWorkflowFlowType(workflow) === FlowType.Cloud;
const isOfTypeScheduledCloud = (workflow) => getWorkflowFlowType(workflow) === FlowType.Cloud && !!workflow.is_scheduled_trigger;
const isOfTypeSubFlow = (workflow) => getWorkflowFlowType(workflow) === FlowType.Sub;
const isOfTypeTile = (workflow) => isOfTypeFeature(workflow) && isTileElement(workflow?.current_draft?.ui_element?.type);
const isOfTypeSlack = (workflow) => isSlackTool(workflow);
const isNotification = (workflow) => workflow.is_notification;

const isCustomerAuthEnabled = (workflow) => workflow?.current_draft?.settings?.customer_auth_enabled;

const getWorkflowAccess = (workflow) => workflow?.access_type;
const isPublicAccess = (workflow) => getWorkflowAccess(workflow) === WorkflowAccessType.Anyone;
const isWorkspaceAccess = (workflow) => getWorkflowAccess(workflow) === WorkflowAccessType.WorkspaceUsers;
const isSharedUsersAccess = (workflow) => getWorkflowAccess(workflow) === WorkflowAccessType.SharedUsersOnly;

const getRunLink = (workflow) => new URL(workflow.run_link).href;

const isToggleAddOnToggleable = (flowType, isCommandBarEnabled) =>
	flowType === FlowType.Experience || (flowType === FlowType.Tool && isCommandBarEnabled);

const getElementPlacement = (workflow, getPlacement, getPlacementVersion) => ({
	placement: getPlacement(workflow?.current_draft?.ui_element?.placement),
	version: getPlacementVersion(workflow?.current_draft?.ui_element?.placement, workflow?.current_draft?.ui_element?.version),
});

const getFlowLocation = (workflow, getServiceBySlug, getPlacement, getPlacementVersion) => {
	const appName = getServiceBySlug(workflow?.context_service_slug)?.service_name;
	const isCommandBar = isCommandBarTool(workflow);
	const isInSlack = isSlackTool(workflow);

	if (isNotification(workflow)) {
		return `from ${appName}`;
	}
	const flowType = workflow.flow_type;
	switch (flowType) {
		case FlowType.Experience: {
			const { placement } = getElementPlacement(workflow, getPlacement, getPlacementVersion);
			const contextLocation = placement?.item_name;
			return `in ${appName} ${contextLocation?.toLowerCase()}`;
		}
		case FlowType.Tool:
			if (isCommandBar) {
				return `in the ${getToolPlacementTitle(workflow)}`;
			} else if (isInSlack) {
				return `in ${getToolPlacementTitle(workflow)}`;
			} else {
				return `at a ${getToolPlacementTitle(workflow)}`;
			}
		case FlowType.Cloud:
			return 'triggered by';
		default:
			break;
	}
	return '';
};

const getReadableFlowType = (workflow, getPlacement, getPlacementVersion) => {
	if (isNotification(workflow)) {
		if (isCustomerNotification(workflow)) {
			return 'Customer notification';
		}
		return 'Internal notification';
	}
	const flowType = workflow.flow_type;
	switch (flowType) {
		case FlowType.Experience: {
			const { placement } = getElementPlacement(workflow, getPlacement, getPlacementVersion);
			return `${placement?.is_piggyback ? 'On-send checkbox' : capitalizeWord(workflow.current_draft.ui_element.type)} add-on`;
		}
		case FlowType.Tool:
			return isPublicAccess(workflow) ? 'Portal' : 'Tool';
		case FlowType.Cloud:
			return 'Automation';
		case FlowType.Sub:
			return 'Subflow';
		default:
			break;
	}
	return '';
};

const isPortal = (workflow) => isOfTypeTool(workflow) && isPublicAccess(workflow);
const isCustomerNotification = (workflow) => isOfTypeCloud(workflow) && isPublicAccess(workflow) && isNotification(workflow);
const isCustomerFacingFeature = (workflow) => isPortal(workflow) || isCustomerNotification(workflow);

const isActionAllowedInWorkflow = (serviceSlug, isNotification) => {
	const isSubflowAllowed = !isNotification;
	const result = isSubflowAllowed || serviceSlug !== ServiceSlugs.SubFlow;
	return result;
};

const buildTriggerURL = (runLink, testParams) => {
	const url = new URL(runLink);
	Object.entries(testParams).forEach(([key, value]) => {
		url.searchParams.set(key, value);
	});
	return url;
};

const WorkflowService = {
	canHideWorkflow,
	isWorkflowShareable,
	getWorkflowName,
	getGlobalMappedFields,
	isWorkflowVersionOfWorkflow,
	getWorkflowContextServiceSlug,
	mapNodesOfWorkflowByField,
	getUserConfigurationFields,
	getGlobalConfigurationFields,
	getConfigurationFields,
	getFlowLocation,
	getReadableFlowType,
	getWorkflowFlowType,
	isOfTypeTool,
	isOfTypeFeature,
	isOfTypeCloud,
	isOfTypeScheduledCloud,
	isOfTypeSubFlow,
	isOfTypeSlack,
	getWorkflowAccess,
	isPublicAccess,
	isWorkspaceAccess,
	isSharedUsersAccess,
	getElementPlacement,
	getRunLink,
	isToggleAddOnToggleable,
	isCustomerFacingFeature,
	isActionAllowedInWorkflow,
	isOfTypeTile,
	needToShowWorkflow,
	buildTriggerURL,
	isCustomerAuthEnabled,
};

export default WorkflowService;
